// ─────────────────────────────────────────────────────────
// Einstellungen → Feiertage (gesetzliche Feiertage pro Kanton, plancraft-Stil)
// ─────────────────────────────────────────────────────────
// Eigenes Modul. Nutzt /api/holidays (org-scoped, holidays:read/write).
// Kanton wählen → Feiertage werden per date-holidays (Backend) generiert, einzeln
// aktivierbar. Speist später die Zeiterfassung (TimeEntry.HOLIDAY).
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
// Ausnahme: die Feiertags-NAMEN kommen aus date-holidays (Backend) und sind Daten,
// keine UI-Strings — sie werden nicht übersetzt.
import { icon, escapeHtml } from '../../utils/dom.js';
import { generateHolidays, saveHolidays } from '../../core/org.js';
import { date } from '../../utils/format.ts';
import { t } from '../../i18n/index.js';

// YYYY-MM-DD → DD.MM.YYYY. format.ts liest reine Datumsangaben lokal statt UTC
// (sonst zeigte ein Feiertag westlich von Greenwich den Vortag).
const fmtDate = (iso) => date(String(iso ?? '').slice(0, 10));

function cantonName(cantons, region) {
  if (!region) return null;
  return (cantons && cantons[region]) || region;
}

// ── Listen-Bereich ────────────────────────────────────────
export function renderHolidaysSection(ctx) {
  const { s, can } = ctx;
  if (!can('holidays:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.hdLoading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const data = s.holidayData || { region: null, year: new Date().getFullYear(), holidays: [], cantons: {} };
  const canton = cantonName(data.cantons, data.region);
  const active = (data.holidays || []).filter((h) => h.isActive);

  const rows = active.length
    ? active
        .map(
          (h) => `
        <div class="cd-hd-trow">
          <div>${escapeHtml(h.name)}</div>
          <div class="cd-hd-date">${escapeHtml(fmtDate(h.date))}</div>
        </div>`,
        )
        .join('')
    : `<div class="cd-emp-empty">${escapeHtml(canton ? t('settings.holidays.noneActive') : t('settings.holidays.noCanton'))}</div>`;

  const cantonBlock = canton
    ? `<div class="cd-hd-canton"><span class="cd-hd-label">${escapeHtml(t('settings.holidays.canton'))}</span><span>${escapeHtml(canton)}</span></div>`
    : '';

  const head = `<div class="cd-hd-thead"><span>${escapeHtml(t('settings.holidays.headYear', { year: data.year }))}</span><span class="cd-hd-date">${escapeHtml(t('settings.holidays.date'))}</span></div>`;

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-hd-cardhead">
          <div class="cd-emp-card-head">${icon('celebration', 18)}<h3>${escapeHtml(t('settings.holidays.title'))}</h3></div>
          ${can('holidays:write') ? `<button class="cd-btn-ghost" data-action="hd-edit-open">${icon('edit', 15)}${escapeHtml(t('common.edit'))}</button>` : ''}
        </div>
        ${cantonBlock}
        <div class="cd-emp-table cd-hd-table">${head}${rows}</div>
      </div>
    </div>`;
}

// ── Modal (Kanton + Aktivierung) ──────────────────────────
export function renderHolidayModal(ctx) {
  const { s } = ctx;
  const e = s.hdEdit;
  if (!e || !e.open) return '';
  const dis = e.saving ? 'disabled' : '';
  const cantons = e.cantons || {};
  const cantonOptions = [`<option value="">${escapeHtml(t('settings.holidays.cantonSelect'))}</option>`]
    .concat(
      Object.keys(cantons)
        .sort((a, b) => cantons[a].localeCompare(cantons[b]))
        .map((code) => `<option value="${escapeHtml(code)}" ${e.region === code ? 'selected' : ''}>${escapeHtml(cantons[code])}</option>`),
    )
    .join('');

  let listBody;
  if (e.loadingCanton) {
    listBody = `<div class="cd-hd-loading">${escapeHtml(t('settings.holidays.loadingCanton'))}</div>`;
  } else if (!(e.holidays || []).length) {
    listBody = `<div class="cd-emp-empty">${escapeHtml(t('settings.holidays.pickCanton'))}</div>`;
  } else {
    listBody = e.holidays
      .map(
        (h, i) => `
        <label class="cd-hd-crow">
          <span>${escapeHtml(h.name)}</span>
          <span class="cd-hd-date">${escapeHtml(fmtDate(h.date))}</span>
          <input type="checkbox" id="hd-day-${i}" ${h.isActive ? 'checked' : ''} ${dis}>
        </label>`,
      )
      .join('');
  }

  return `
    <div class="cd-emp-modal-overlay" data-action="hd-close">
      <div class="cd-emp-modal cd-hd-modal" data-action="hd-noop">
        <div class="cd-emp-modal-head">
          <h2>${icon('celebration', 20)}${escapeHtml(t('settings.holidays.title'))}</h2>
          <div class="cd-set-head-actions">
            <button class="cd-btn-ghost" data-action="hd-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
            <button class="cd-btn-primary" data-action="hd-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>
          </div>
        </div>
        <div class="cd-emp-modal-body">
          ${e.error ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(e.error)}</span></div>` : ''}
          <div class="cd-emp-modal-hint">${icon('info', 15)}<span>${escapeHtml(t('settings.holidays.hint'))}</span></div>
          <label class="cd-field">
            <span>${escapeHtml(t('settings.holidays.canton'))}</span>
            <div class="cd-select-shell">
              <select class="cd-input" id="hd-canton" ${dis}>${cantonOptions}</select>
              ${icon('expand_more', 18)}
            </div>
          </label>
          <div class="cd-hd-table">
            <div class="cd-hd-chead"><span>${escapeHtml(t('settings.holidays.headYear', { year: e.year }))}</span><span class="cd-hd-date">${escapeHtml(t('settings.holidays.date'))}</span><span>${escapeHtml(t('settings.holidays.activated'))}</span></div>
            ${listBody}
          </div>
        </div>
      </div>
    </div>`;
}

// ── Kanton-Wechsel im Modal (aus dem change-Listener) ─────
export function handleHolidayCantonChange(value, ctx) {
  const { s, draw } = ctx;
  const e = s.hdEdit;
  if (!e) return;
  e.region = value || null;
  if (!e.region) { e.holidays = []; draw(); return; }
  e.loadingCanton = true; e.error = ''; draw();
  generateHolidays(e.region, e.year)
    .then((r) => {
      if (s.hdEdit && s.hdEdit.region === e.region) {
        s.hdEdit.holidays = r.holidays || [];
        s.hdEdit.loadingCanton = false;
        draw();
      }
    })
    .catch((err) => {
      if (s.hdEdit) { s.hdEdit.loadingCanton = false; s.hdEdit.error = err.message || t('settings.holidays.loadFailed'); draw(); }
    });
}

// ── Aktions-Handler ───────────────────────────────────────
export function handleHolidayAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  switch (a) {
    case 'hd-noop':
      return true;
    case 'hd-edit-open': {
      if (!ctx.can('holidays:write')) return true;
      const d = s.holidayData || { region: null, year: new Date().getFullYear(), holidays: [], cantons: {} };
      s.hdEdit = {
        open: true, region: d.region, year: d.year, cantons: d.cantons || {},
        holidays: (d.holidays || []).map((h) => ({ date: h.date, name: h.name, isActive: h.isActive })),
        loadingCanton: false, saving: false, error: '',
      };
      draw();
      return true;
    }
    case 'hd-close':
      s.hdEdit = null;
      draw();
      return true;
    case 'hd-submit': {
      const e = s.hdEdit;
      if (!e) return true;
      const region = document.getElementById('hd-canton')?.value || null;
      const holidays = (e.holidays || []).map((h, i) => ({
        date: h.date,
        name: h.name,
        isActive: !!document.getElementById('hd-day-' + i)?.checked,
      }));
      e.saving = true; e.error = ''; draw();
      saveHolidays({ region, year: e.year, holidays })
        .then(() => { s.hdEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.hdEdit) { s.hdEdit.saving = false; s.hdEdit.error = err.message || t('common.saveFailed'); } draw(); });
      return true;
    }
    default:
      return false;
  }
}
